/**
 * "Agora" e "Atividade" ao vivo: decisões e feed montados com os dados reais, no lugar da simulação.
 *
 * Decisões vêm de duas fontes:
 *  - alertas do servidor (`GET /api/automacao/alerts`): fila da equipe, e-mail de avisos, IA, envios;
 *  - regras de mídia sobre a Meta real dos últimos 30 dias (mesmas do relatório diário): campanha com
 *    3 dias ou mais sem venda, campanha nova gastando sem vender, campanha para escalar (ROAS ≥ 4),
 *    retorno baixo e conta que gasta mais do que vende. Vendas de anúncio = compras que a Meta atribui
 *    (Gerenciador); ROAS = valor dessas compras ÷ valor gasto na Meta (sem impostos), o mesmo
 *    "ROAS de compras" do Gerenciador. A Kiwify pelo link (UTM) vai só como referência.
 * O que a equipe marca (feito, adiado, descartado) fica no servidor (`/api/painel/decisoes`), por
 * chave do dia: amanhã a decisão volta se o problema continuar.
 * A conta de anúncios só tem permissão de leitura: nenhuma decisão executa nada na Meta.
 */

import type {
  ActivityItem,
  AutomationAlert,
  ChangesSince,
  Decision,
  DecisionCommand,
  DecisionStatus,
  Evidence,
  ListQuery,
  Page,
  RuleDef,
  RuleScope,
  Severity,
} from '@rota/contracts';
import { getWithRetry } from './http';
import {
  CACHE_MS,
  dayStart,
  isRefundLike,
  orderDates,
  paginate,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  revenueOf,
  todayKey,
} from './realCore';
import { type RealInsightRow, withMetaSales } from './realTraffic';

const DAY = 86_400_000;
const BRT = 3 * 3_600_000;
/** Janela das regras de mídia: hoje e os 29 dias antes (dias de São Paulo). */
export const MEDIA_WINDOW_DAYS = 30;
/**
 * Alvo das decisões da automação: a área Automações da suíte, fora do Painel. O tipo ainda não está
 * no contrato (`RuleScope`): o sheet e o cartão só leem o rótulo e a rota.
 */
export const AUTOMACAO_TARGET = 'automacao' as RuleScope;
/** Contas de anúncios conhecidas (nome curto para os títulos). */
const ACCOUNT_NAMES: Record<string, string> = {
  act_451177072626810: 'CA 01',
  act_1548350927048415: 'CA 02',
  act_2532436727231820: 'YO 98',
};
const accountName = (id: string) => ACCOUNT_NAMES[id] ?? id.replace(/^act_/, 'conta ');

/** Retrato da decisão: o que o Histórico mostra depois que a regra deixa de disparar. */
export interface DecisionSnap {
  title: string;
  ruleId: Decision['ruleId'];
  severity: Severity;
  kind: Decision['kind'];
  rationale?: string;
  target?: Decision['target'];
  impact: Decision['impact'];
  createdAt: number;
}

export interface DecisionState {
  status: DecisionStatus;
  at: number;
  by?: string;
  until?: number;
  reason?: string;
  /** Retrato salvo na hora da marca (estados antigos não têm). */
  snap?: DecisionSnap;
}
export type DecisionStates = Record<string, DecisionState>;

/** 1ª e última vez que o Painel viu a decisão (servidor), com o retrato mais recente. */
export interface SeenEntry {
  firstAt: number;
  lastAt: number;
  snap?: DecisionSnap | null;
}
export type SeenMap = Record<string, SeenEntry>;

/** Alerta como o servidor manda (com quem resolveu: 'auto' = sumiu sozinho). */
export type ServerAlert = AutomationAlert & { resolvedBy?: string | null };
export interface ServerAlerts {
  open: ServerAlert[];
  /** Resolvidos nos últimos 7 dias. */
  resolved: ServerAlert[];
}

// ---------- servidor ----------

/** Estados marcados pela equipe. Erro do servidor lança: quem chama mantém o último estado conhecido. */
export async function fetchDecisionStates(): Promise<DecisionStates> {
  const r = await getWithRetry('/api/painel/decisoes');
  if (!r.ok) throw new Error(`decisões ${r.status}`);
  return ((await r.json()) as { states?: DecisionStates }).states ?? {};
}

export async function saveDecisionState(
  key: string,
  status: DecisionStatus,
  extra: { until?: number; reason?: string; snap?: DecisionSnap } = {},
): Promise<void> {
  const r = await fetch('/api/painel/decisoes', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ key, status, ...extra }),
  });
  if (!r.ok) throw new Error(`decisão ${r.status}`);
}

/** Avisa o servidor das decisões na tela; devolve a 1ª e a última vez de cada uma. */
export async function markSeen(items: { key: string; snap: DecisionSnap }[]): Promise<SeenMap> {
  const r = await fetch('/api/painel/decisoes/vistas', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!r.ok) throw new Error(`decisões vistas ${r.status}`);
  return ((await r.json()) as { seen?: SeenMap }).seen ?? {};
}

/** Decisões vistas no intervalo (com o retrato), para o histórico e o "O que mudou". */
export async function fetchSeen(from: number, to: number): Promise<SeenMap> {
  const r = await getWithRetry(`/api/painel/decisoes/vistas?from=${from}&to=${to}`);
  if (!r.ok) throw new Error(`decisões vistas ${r.status}`);
  return ((await r.json()) as { seen?: SeenMap }).seen ?? {};
}

/** Alertas abertos e resolvidos (7 dias). Erro lança: quem chama fica com a última lista boa. */
export async function fetchAlerts(): Promise<ServerAlerts> {
  const r = await getWithRetry('/api/automacao/alerts');
  if (!r.ok) throw new Error(`alertas ${r.status}`);
  const b = (await r.json()) as Partial<ServerAlerts>;
  return { open: b.open ?? [], resolved: b.resolved ?? [] };
}

export async function fetchOpenAlerts(): Promise<AutomationAlert[]> {
  const r = await getWithRetry('/api/automacao/alerts');
  if (!r.ok) return [];
  return ((await r.json()) as { open?: AutomationAlert[] }).open ?? [];
}

export async function ackAlert(id: string): Promise<void> {
  await fetch(`/api/automacao/alerts/${encodeURIComponent(id)}/ack`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
}

// ---------- formatação ----------

const brl = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const ratio = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;
const dateKeyOf = (t: number) => new Date(t - BRT).toISOString().slice(0, 10);

function base(
  p: Pick<Decision, 'ruleId' | 'severity' | 'kind' | 'title' | 'rationale' | 'whyText' | 'evidence'> & {
    key: string;
    target?: Decision['target'];
    impact: Decision['impact'];
    action: Decision['action'];
    createdAt: number;
    priority: number;
    owners?: Decision['owners'];
    lastSeenAt?: number;
  },
): Decision {
  return {
    id: p.key,
    ruleId: p.ruleId,
    dedupKey: p.key,
    severity: p.severity,
    kind: p.kind,
    confidence: 'high',
    falseAlarmP: 0,
    title: p.title,
    rationale: p.rationale,
    whyText: p.whyText,
    ...(p.target ? { target: p.target } : {}),
    evidence: p.evidence,
    owners: p.owners ?? ['SO'],
    impact: p.impact,
    priorityScore: p.priority,
    action: p.action,
    status: 'new',
    createdAt: p.createdAt,
    updatedAt: p.createdAt,
    ...(p.lastSeenAt != null ? { lastSeenAt: p.lastSeenAt } : {}),
    history: [],
  };
}

// ---------- regras de mídia ----------

export interface MediaRuleInput {
  /**
   * Campanhas nos últimos 30 dias (gasto, compras e valor somados), como o servidor manda: as
   * compras do Gerenciador da Meta (`metaPurchases`) entram aqui no lugar das vendas pelo link.
   */
  campaigns: RealInsightRow[];
  /** As mesmas campanhas só hoje (gasto de hoje). */
  today: RealInsightRow[];
  now: number;
  /** Início da janela (dia de São Paulo); sem ele, o título diz "em 30 dias". */
  from?: number;
}

const sales = (r: RealInsightRow) => r.frontSales + r.upsellSales;
/** ROAS do Gerenciador: valor das compras que a Meta atribui ÷ valor gasto na Meta (sem impostos). */
const roasOf = (r: { grossCents: number; spendCents: number }) =>
  r.spendCents > 0 ? r.grossCents / r.spendCents : 0;
/** A linha trouxe as compras da Meta (servidor antigo só tinha as vendas pelo link da Kiwify). */
const hasMeta = (r: RealInsightRow) => r.metaPurchases != null && r.metaValueCents != null;

/**
 * Orçamento diário em centavos de real. O servidor já manda a conta em dólar convertida pela PTAX do
 * dia (a mesma cotação do gasto). Servidor antigo mandava a moeda da conta: sem câmbio confiável
 * aqui, fica sem orçamento (nunca um câmbio fixo inventado).
 */
function budgetBrl(r: RealInsightRow): number | null {
  if (r.dailyBudget == null || r.dailyBudget <= 0) return null;
  return r.currency === 'USD' ? null : r.dailyBudget;
}

/** "US$ 46,00" (orçamento como está na Meta, conta em dólar). */
const usd = (cents: number) =>
  `US$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function mediaDecisions({ campaigns, today, now, from }: MediaRuleInput): Decision[] {
  const dk = dateKeyOf(now);
  const todayById = new Map(today.map((r) => [r.id, r]));
  const out: Decision[] = [];
  const target = (r: RealInsightRow): Decision['target'] => ({
    type: 'campaign',
    id: r.id,
    name: r.name,
    route: `/trafego/campanha/${r.id}`,
  });
  const ev = (
    label: string,
    value: number,
    format: Evidence['format'],
    source: Evidence['source'] = 'meta',
  ): Evidence => ({
    label,
    value,
    format,
    window: 'last30d',
    source,
  });
  /** "desde 20/10": início da janela ou o dia em que a campanha foi criada, o que vier depois. */
  const sinceOf = (r: RealInsightRow) =>
    from == null ? 'em 30 dias' : `desde ${ddmm(Math.max(from, r.createdAt ?? from))}`;
  for (const raw of campaigns) {
    if (raw.level !== 'campaign' || raw.status !== 'ACTIVE' || raw.spendCents <= 0) continue;
    // Vendas de anúncio = compras do Gerenciador; a Kiwify pelo link (UTM) fica como referência.
    const meta = hasMeta(raw);
    const r = withMetaSales(raw);
    const age = r.createdAt ? (now - r.createdAt) / DAY : 0;
    const n = sales(r);
    const roas = roasOf(r);
    const budget = budgetBrl(r);
    const spentToday = todayById.get(r.id)?.spendCents ?? 0;
    const acct = accountName(r.accountId);
    const evidence = [
      ev('Gasto em 30 dias', r.spendCents, 'brl0'),
      meta
        ? ev('Vendas (Gerenciador da Meta)', n, 'int')
        : ev('Vendas pelo link (Kiwify)', n, 'int', 'kiwify'),
      meta
        ? ev('Faturamento (Gerenciador da Meta)', r.grossCents, 'brl0')
        : ev('Faturamento pelo link (Kiwify)', r.grossCents, 'brl0', 'kiwify'),
      ev('ROAS', roas, 'ratio2'),
      ...(meta ? [ev('Vendas pelo link (Kiwify, referência)', sales(raw), 'int', 'kiwify')] : []),
      ev('Visitas à página', r.lpv, 'int'),
    ];
    // Conta em dólar: o orçamento como está na Meta, para quem vai mexer lá.
    const native =
      r.accountCurrency === 'USD' && r.budgetNative != null && r.budgetNative > 0 ? r.budgetNative : null;
    if (age >= 3 && n === 0 && r.spendCents >= 5_000) {
      out.push(
        base({
          key: `RM1:${r.id}:${dk}`,
          ruleId: 'RM1',
          severity: 'P1',
          kind: 'risk',
          title: `Pausar ${r.name}: ${Math.floor(age)} dias sem venda`,
          rationale: `${acct} · gastou ${brl(r.spendCents)} nos últimos 30 dias (${brl(spentToday)} hoje) e não teve nenhuma compra no Gerenciador da Meta.`,
          whyText:
            'Campanha ativa há 3 dias ou mais, sem nenhuma compra registrada no Gerenciador da Meta nos últimos 30 dias. A verba rende mais nas campanhas que já vendem. Pause no Gerenciador de Anúncios e marque como feito.',
          evidence,
          target: target(r),
          impact: {
            cents: budget ?? spentToday,
            label: budget
              ? `≈ ${brl(budget)}/dia de orçamento liberado`
              : `${brl(spentToday)} gastos hoje sem venda`,
            calc: budget
              ? `orçamento diário da campanha${native ? ` (${usd(native)} na Meta, pela PTAX do dia)` : ''}`
              : 'gasto de hoje da campanha',
            horizon: 'day',
          },
          action: {
            kind: 'pause',
            target: { type: 'campaign', id: r.id, name: r.name },
            executable: false,
            guardrails: [],
          },
          createdAt: now,
          priority: 80 + Math.min(19, r.spendCents / 10_000),
          owners: ['SO', 'GT'],
        }),
      );
    } else if (age < 3 && n === 0 && r.spendCents >= 10_000) {
      out.push(
        base({
          key: `RM5:${r.id}:${dk}`,
          ruleId: 'RM5',
          severity: 'P2',
          kind: 'risk',
          title: `${r.name}: ${brl(r.spendCents)} gastos e nenhuma venda ainda`,
          rationale: `${acct} · campanha nova (menos de 3 dias), sem compra no Gerenciador da Meta. Se não vender até o fim do dia, pausar.`,
          whyText:
            'Ainda não completou 3 dias, mas já gastou R$ 100 ou mais sem nenhuma compra no Gerenciador da Meta. Vale acompanhar e pausar à noite se seguir sem vender.',
          evidence,
          target: target(r),
          impact: {
            cents: spentToday,
            label: `${brl(spentToday)} gastos hoje`,
            calc: 'gasto de hoje',
            horizon: 'today',
          },
          action: { kind: 'investigate', executable: false, guardrails: [] },
          createdAt: now,
          priority: 50 + Math.min(19, r.spendCents / 10_000),
          owners: ['SO', 'GT'],
        }),
      );
    } else if (n >= 2 && roas >= 4) {
      const to = budget ? Math.round(budget * 1.2) : null;
      const extra = to && budget ? to - budget : 0;
      const since = sinceOf(r);
      // Projeção, não dinheiro em jogo: o faturamento a mais por dia se o ROAS se mantiver depois de
      // subir o orçamento (o DEC-04 mostra à parte, fora do "em jogo hoje").
      const projected = Math.round(extra * roas);
      const nativeTo = native ? `${usd(native)} → ${usd(Math.round(native * 1.2))} na Meta` : '';
      const nativeStep = nativeTo ? ` (${nativeTo})` : '';
      out.push(
        base({
          key: `RM2:${r.id}:${dk}`,
          ruleId: 'RM2',
          severity: 'P2',
          kind: 'opportunity',
          title: `Escalar ${r.name}: ROAS ${ratio(roas)} ${since}`,
          rationale: `${acct} · ${n} vendas no Gerenciador da Meta, ${brl(r.grossCents)} de faturamento para ${brl(r.spendCents)} gastos ${since}. Subir 20 a 30% o orçamento${nativeStep}, uma vez por dia.`,
          whyText:
            'Campanha com ROAS de 4x ou mais e pelo menos 2 compras no Gerenciador da Meta nos últimos 30 dias. ROAS = valor das compras que a Meta atribui ÷ valor gasto na Meta (sem impostos). Subir em degraus de 20 a 30% por dia, conferindo o ROAS do dia anterior, para não quebrar o aprendizado da Meta.',
          evidence,
          target: target(r),
          impact: {
            cents: projected,
            label:
              extra > 0
                ? `≈ +${brl(projected)}/dia de faturamento (+${brl(extra)}/dia de orçamento)`
                : 'mais verba no que vende',
            calc:
              extra > 0
                ? `faturamento extra estimado por dia: +20% do orçamento (+${brl(extra)}/dia${nativeTo ? `, ${nativeTo}` : ''}) × ROAS ${since} (se o ROAS se mantiver)`
                : 'sem orçamento diário na campanha (orçamento no conjunto): sem estimativa',
            horizon: 'day',
          },
          action: {
            kind: 'budget_change',
            target: { type: 'campaign', id: r.id, name: r.name },
            ...(budget && to ? { params: { budgetFrom: budget, budgetTo: to, deltaPct: 0.2 } } : {}),
            executable: false,
            guardrails: [],
          },
          createdAt: now,
          priority: 40 + Math.min(39, roas * 3),
          owners: ['SO', 'GT'],
        }),
      );
    } else if (age >= 3 && n >= 1 && roas < 1.5) {
      out.push(
        base({
          key: `RM4:${r.id}:${dk}`,
          ruleId: 'RM4',
          severity: 'P2',
          kind: 'risk',
          title: `${r.name}: ROAS ${ratio(roas)}, quase não paga o gasto`,
          rationale: `${acct} · ${n} ${n === 1 ? 'venda' : 'vendas'} no Gerenciador da Meta e ${brl(r.grossCents)} para ${brl(r.spendCents)} gastos nos últimos 30 dias. Segurar o orçamento e pausar se não melhorar.`,
          whyText:
            'Campanha com 3 dias ou mais e ROAS abaixo de 1,5x nos últimos 30 dias (valor das compras da Meta ÷ valor gasto): a venda paga pouco mais que o anúncio.',
          evidence,
          target: target(r),
          impact: { cents: 0, label: 'segurar a verba', calc: '', horizon: 'day' },
          action: {
            kind: 'hold_scaling',
            target: { type: 'campaign', id: r.id, name: r.name },
            executable: false,
            guardrails: [],
          },
          createdAt: now,
          priority: 45,
          owners: ['SO', 'GT'],
        }),
      );
    }
  }
  // Conta que gasta mais do que vende (nos últimos 30 dias).
  const byAcct = new Map<string, { spendCents: number; grossCents: number; n: number }>();
  for (const raw of campaigns) {
    const r = withMetaSales(raw);
    const a = byAcct.get(r.accountId) ?? { spendCents: 0, grossCents: 0, n: 0 };
    a.spendCents += r.spendCents;
    a.grossCents += r.grossCents;
    a.n += sales(r);
    byAcct.set(r.accountId, a);
  }
  for (const [id, a] of byAcct) {
    const roas = roasOf(a);
    if (a.spendCents < 50_000 || roas >= 1) continue;
    const acct = accountName(id);
    const loss = Math.max(0, a.spendCents - a.grossCents);
    out.push(
      base({
        key: `RM3:${id}:${dk}`,
        ruleId: 'RM3',
        severity: 'P1',
        kind: 'risk',
        title: `Conta ${acct} gasta mais do que vende: ROAS ${ratio(roas)}`,
        rationale: `${brl(a.spendCents)} gastos e ${brl(a.grossCents)} em vendas (${a.n} no Gerenciador da Meta) nos últimos 30 dias. Reduzir o orçamento e manter só o que vende.`,
        whyText:
          'Somando todas as campanhas da conta nos últimos 30 dias, o valor das compras que a Meta atribui não cobre o valor gasto. A verba rende mais nas campanhas das outras contas que já vendem.',
        evidence: [
          ev('Gasto em 30 dias', a.spendCents, 'brl0'),
          ev('Faturamento (Gerenciador da Meta)', a.grossCents, 'brl0'),
          ev('ROAS', roas, 'ratio2'),
        ],
        impact: {
          cents: loss,
          label: `${brl(loss)} a mais gastos do que vendidos em 30 dias`,
          calc: 'gasto − faturamento (Gerenciador da Meta) nos últimos 30 dias',
          // Soma de 30 dias: fica fora do "em jogo hoje".
          horizon: 'month',
        },
        action: { kind: 'investigate', executable: false, guardrails: [] },
        createdAt: now,
        priority: 90,
        owners: ['SO', 'GT'],
      }),
    );
  }
  return out;
}

// ---------- alertas do servidor ----------

const ALERT_SEVERITY: Record<AutomationAlert['severity'], Severity> = {
  critical: 'P1',
  warning: 'P2',
  info: 'P3',
};

/**
 * Rota do botão do alerta. O servidor manda caminhos da área Automações (`/automacoes/conversas…`),
 * que fica fora do Painel (as rotas do Painel vivem sob `/painel`): o `~` faz o link sair da base.
 */
export function alertRoute(href: string | null | undefined): string {
  if (href?.startsWith('~/')) return href;
  if (href?.startsWith('/')) return `~${href}`;
  return '~/automacoes';
}

/** Alvo do alerta: a área Automações, com a ação do alerta ("Responder agora") como nome. */
function alertTarget(id: string, label: string | null | undefined, href: string | null | undefined) {
  return { type: AUTOMACAO_TARGET, id, name: label || 'Abrir Automações', route: alertRoute(href) };
}

/**
 * Retrato antigo (antes do `~`): alvo do tipo "Dia" com a rota da Automação tratada como rota do
 * Painel. Volta para o alvo da área Automações.
 */
function fixAlertTarget(t: Decision['target'] | undefined): Decision['target'] | undefined {
  if (!t || !/^\/automacoes(\/|\?|$)/.test(t.route)) return t;
  return { ...t, type: AUTOMACAO_TARGET, route: alertRoute(t.route) };
}

/**
 * Prioridade do alerta na mesma régua das regras de mídia (Escalar vai de 52 a 79): perda iminente
 * (janela de 24 h fechando, fila do direct) acima das escalas; aviso de configuração parado há dias
 * (e-mail de avisos, trava de voz) abaixo delas, sem sair do "Agora". Crítico fica sempre no topo.
 */
const ALERT_PRIORITY: Record<string, number> = {
  window_closing: 80,
  dm_backlog: 76,
  comments_expiring: 74,
  send_failures: 70,
  ai_retries: 66,
  pr_backlog: 50,
  smtp_missing: 35,
  voice_guard: 35,
  config_copy_dropped: 35,
  db_growth: 35,
};

export function alertPriority(a: Pick<AutomationAlert, 'rule' | 'severity'>): number {
  if (a.severity === 'critical') return 95;
  return ALERT_PRIORITY[a.rule] ?? (a.severity === 'warning' ? 60 : 30);
}

/** "12:40" no fuso de São Paulo. */
const hhmm = (t: number) => new Date(t - BRT).toISOString().slice(11, 16);
/** "25/10" no fuso de São Paulo. */
const ddmm = (t: number) => {
  const iso = new Date(t - BRT).toISOString();
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
};

/** Impacto do alerta da automação: sem valor em R$. */
const ALERT_IMPACT: Decision['impact'] = {
  cents: 0,
  label: 'operação da automação',
  calc: '',
  horizon: 'once',
};

export function alertDecisions(alerts: AutomationAlert[], now: number): Decision[] {
  return alerts.map((a) =>
    base({
      key: `RA:${a.rule}:${a.id}`,
      ruleId: `RA-${a.rule}`,
      severity: ALERT_SEVERITY[a.severity] ?? 'P2',
      kind: 'risk',
      title: a.title,
      rationale: a.detail,
      // O título é reescrito a cada varredura: o número é da última conferência, não da 1ª vez.
      whyText: `Alerta da automação do Instagram (${a.area}), aberto desde ${ddmm(a.firstSeenAt)} às ${hhmm(
        a.firstSeenAt,
      )}. O número do título é da última conferência, às ${hhmm(
        Math.min(a.lastSeenAt, now),
      )}. Marcar como feito silencia o alerta; se o problema continuar, ele volta.`,
      evidence: [],
      target: alertTarget(a.id, a.action?.label, a.action?.href),
      impact: { ...ALERT_IMPACT },
      action: { kind: 'investigate', executable: false, guardrails: [] },
      // A idade (e o "Atrasada") conta da 1ª vez que o problema apareceu; a conferência vai à parte.
      createdAt: Math.min(a.firstSeenAt, now),
      lastSeenAt: Math.min(a.lastSeenAt, now),
      priority: alertPriority(a),
      owners: ['SO', 'SR'],
    }),
  );
}

// ---------- catálogo das regras que rodam de verdade ----------

type Param = RuleDef['params'][string];
const param = (
  value: number,
  unit: Param['unit'],
  label: string,
  range: [min: number, max: number, step: number],
): Param => ({ value, default: value, unit, label, min: range[0], max: range[1], step: range[2] });

const ruleDef = (
  p: Pick<RuleDef, 'id' | 'name' | 'description' | 'category' | 'scope' | 'severity' | 'kind' | 'owners'> &
    Partial<Pick<RuleDef, 'escalation' | 'params' | 'actionTemplate' | 'impactFormula'>>,
): RuleDef => ({
  cadence: '1m',
  persistence: { evaluations: 1, minGapMin: 0 },
  minSample: [],
  cooldownMin: 0,
  params: {},
  actionTemplate: null,
  impactFormula: '',
  enabled: true,
  ...p,
});

const MEDIA_NOTE =
  ' Avaliada a cada minuto com os números da última sincronização da Meta, nos últimos 30 dias. Uma campanha cai numa regra só, nesta ordem: pausar, nova sem venda, escalar, retorno baixo.';

/**
 * Regras de mídia (RM1–RM5) com os limiares que `mediaDecisions` usa. Só leitura: mudar o número aqui
 * não muda a regra (o relatório diário do servidor usa os mesmos).
 */
const MEDIA_RULES: RuleDef[] = [
  ruleDef({
    id: 'RM1',
    name: 'Pausar campanha sem venda',
    description: `Campanha ativa há 3 dias ou mais, com R$ 50 ou mais gastos e nenhuma compra no Gerenciador da Meta.${MEDIA_NOTE}`,
    category: 'midia_cortar',
    scope: 'campaign',
    severity: 'P1',
    kind: 'risk',
    owners: ['SO', 'GT'],
    params: {
      dias: param(3, 'n', 'Dias de campanha ativa (mínimo)', [1, 14, 1]),
      gasto: param(50, 'brl', 'Gasto mínimo em 30 dias', [10, 1000, 10]),
    },
    actionTemplate: 'pause',
    impactFormula: 'Orçamento diário da campanha: o que deixa de gastar por dia ao pausar.',
  }),
  ruleDef({
    id: 'RM5',
    name: 'Campanha nova sem venda',
    description: `Campanha ativa há menos de 3 dias que já gastou R$ 100 ou mais sem nenhuma compra no Gerenciador da Meta. Acompanhar e pausar à noite se seguir sem vender.${MEDIA_NOTE}`,
    category: 'midia_cortar',
    scope: 'campaign',
    severity: 'P2',
    kind: 'risk',
    owners: ['SO', 'GT'],
    params: {
      dias: param(3, 'n', 'Campanha nova: menos de (dias)', [1, 14, 1]),
      gasto: param(100, 'brl', 'Gasto mínimo sem venda', [10, 1000, 10]),
    },
    actionTemplate: 'investigate',
    impactFormula: 'Gasto de hoje da campanha.',
  }),
  ruleDef({
    id: 'RM2',
    name: 'Escalar campanha',
    description: `Campanha ativa com 2 compras ou mais e ROAS de 4x ou mais. ROAS = valor das compras que a Meta atribui ÷ valor gasto na Meta (sem impostos), o mesmo do Gerenciador.${MEDIA_NOTE}`,
    category: 'midia_escalar',
    scope: 'campaign',
    severity: 'P2',
    kind: 'opportunity',
    owners: ['SO', 'GT'],
    params: {
      vendas: param(2, 'n', 'Compras mínimas (Gerenciador da Meta)', [1, 20, 1]),
      roas: param(4, 'x', 'ROAS mínimo', [1, 20, 0.1]),
      degrau: param(0.2, 'pct', 'Aumento do orçamento por dia', [0.05, 0.5, 0.05]),
    },
    actionTemplate: 'budget_change',
    impactFormula:
      'Faturamento extra estimado por dia: aumento do orçamento × ROAS dos últimos 30 dias, se o ROAS se mantiver. É uma estimativa: fica fora do "em jogo hoje".',
  }),
  ruleDef({
    id: 'RM4',
    name: 'Campanha com retorno baixo',
    description: `Campanha com 3 dias ou mais, pelo menos 1 compra no Gerenciador da Meta e ROAS abaixo de 1,5x.${MEDIA_NOTE}`,
    category: 'midia_cortar',
    scope: 'campaign',
    severity: 'P2',
    kind: 'risk',
    owners: ['SO', 'GT'],
    params: {
      dias: param(3, 'n', 'Dias de campanha ativa (mínimo)', [1, 14, 1]),
      vendas: param(1, 'n', 'Compras mínimas (Gerenciador da Meta)', [1, 20, 1]),
      roas: param(1.5, 'x', 'ROAS abaixo de', [0.5, 5, 0.1]),
    },
    actionTemplate: 'hold_scaling',
    impactFormula: 'Sem valor em R$: segurar a verba até o retorno melhorar.',
  }),
  ruleDef({
    id: 'RM3',
    name: 'Conta gasta mais do que vende',
    description:
      'Somando as campanhas da conta nos últimos 30 dias: R$ 500 ou mais gastos e ROAS abaixo de 1x (o valor das compras da Meta não cobre o valor gasto). Avaliada a cada minuto com os números da última sincronização da Meta.',
    category: 'midia_cortar',
    scope: 'account',
    severity: 'P1',
    kind: 'risk',
    owners: ['SO', 'GT'],
    params: {
      gasto: param(500, 'brl', 'Gasto mínimo da conta em 30 dias', [100, 10_000, 100]),
      roas: param(1, 'x', 'ROAS abaixo de', [0.5, 3, 0.1]),
    },
    actionTemplate: 'investigate',
    impactFormula:
      'Gasto − valor das compras (Gerenciador da Meta) nos últimos 30 dias. Fica fora do "em jogo hoje".',
  }),
];

/** Regras de alerta da automação (espelho de `apps/server/src/alerts.ts`: o servidor confere a cada minuto). */
const ALERT_RULES: [
  rule: string,
  name: string,
  severity: Severity,
  description: string,
  escalation?: RuleDef['escalation'],
][] = [
  [
    'ai_failing',
    'IA respondendo',
    'P1',
    'A IA está desligada, sem chave, ou falhou em metade ou mais das respostas da última meia hora (com 5 ou mais).',
  ],
  [
    'ai_retries',
    'Conversas esperando a IA',
    'P2',
    'Mais de 10 conversas presas esperando a IA há 5 min ou mais.',
  ],
  ['voice_guard', 'Trava de voz', 'P2', 'A trava de voz segurou mensagens nas últimas 24 h.'],
  [
    'breaker',
    'Disjuntor de envios',
    'P2',
    'A Meta recusou envios seguidos e o disjuntor parou ou está testando os envios.',
    { to: 'P1', when: 'os envios ficam parados' },
  ],
  ['kill_switch', 'Envios ligados', 'P2', 'Os envios estão pausados: ninguém recebe mensagem até religar.'],
  [
    'send_failures',
    'Falhas de envio (1 h)',
    'P2',
    'Mais de 10% dos envios da última hora falharam (com 10 envios ou mais).',
    { to: 'P1', when: 'passa de 30%' },
  ],
  [
    'webhook_silent',
    'Eventos do Instagram chegando',
    'P2',
    'De dia, nenhum evento do Instagram chegou há mais tempo que o normal.',
    { to: 'P1', when: 'o silêncio passa do limite de queda' },
  ],
  [
    'meta_subscription',
    'Webhooks assinados na Meta',
    'P1',
    'A assinatura dos webhooks na Meta caiu: os eventos do Instagram não chegam.',
  ],
  [
    'token_expiring',
    'Token da Meta',
    'P2',
    'O token da Meta está perto de vencer.',
    { to: 'P1', when: 'o token para de funcionar' },
  ],
  [
    'meta_usage',
    'Uso da API da Meta',
    'P2',
    'Uso da API da Meta acima de 75% do limite.',
    { to: 'P1', when: 'chega a 95%' },
  ],
  [
    'dm_backlog',
    'Fila do direct',
    'P2',
    'Mais de 100 mensagens na fila do direct ou a mais antiga esperando há mais de 10 min.',
    { to: 'P1', when: 'a mais antiga espera há mais de 30 min' },
  ],
  [
    'pr_backlog',
    'Fila de aberturas',
    'P2',
    'Mais de 500 aberturas na fila ou a mais antiga esperando há mais de 6 h.',
  ],
  [
    'comments_expiring',
    'Comentários perto do prazo',
    'P2',
    'Comentários com mais de 5 dias ainda na fila: com 6 dias eles saem sem mensagem.',
  ],
  [
    'handoff_waiting',
    'Pessoas esperando a equipe',
    'P2',
    'Pessoas passadas para a equipe há mais de 2 h sem resposta.',
    { to: 'P1', when: 'tem cliente VIP esperando' },
  ],
  [
    'window_closing',
    'Janelas de 24 h fechando',
    'P2',
    'Conversas sem resposta cuja janela de 24 h da Meta fecha nas próximas 3 h.',
  ],
  [
    'smtp_missing',
    'E-mail de alertas',
    'P2',
    'O e-mail de avisos não está configurado ou o último envio falhou.',
  ],
  [
    'kiwify',
    'Vendas da Kiwify chegando',
    'P2',
    'Os avisos de venda da Kiwify pararam de chegar há mais tempo que o normal, ou foram recusados.',
    { to: 'P1', when: 'o silêncio passa do limite de queda' },
  ],
  [
    'meta_sync_stale',
    'Números da Meta Ads atualizando',
    'P2',
    'A leitura dos números da Meta Ads falhou ou está parada há mais de 30 min.',
  ],
  ['vturb_error', 'Números do vídeo (VTurb)', 'P2', 'A leitura dos números da VTurb falhou.'],
  [
    'config_copy_dropped',
    'Textos da configuração',
    'P2',
    'Textos antigos da configuração foram ignorados no último início.',
  ],
  ['palco_down', 'Player do Palco no ar', 'P2', 'O player do Palco está fora do ar.'],
  [
    'vsl_page_down',
    'Página do vídeo no ar',
    'P1',
    'A página do vídeo está fora do ar: quem clica no link pode não ver o vídeo.',
  ],
  [
    'disk_low',
    'Espaço em disco',
    'P2',
    'Pouco espaço livre no servidor ou previsão de encher em menos de 30 dias.',
    { to: 'P1', when: 'enche em menos de 7 dias' },
  ],
  ['db_growth', 'Tamanho do banco', 'P3', 'O banco cresceu bem acima do normal em 24 h.'],
];

/**
 * Catálogo do modo ao vivo: as regras de mídia (RM1–RM5) e as de alerta da automação (`RA-<regra>`),
 * com os mesmos ids que as decisões gravam em `ruleId` ("Disparou hoje", o nome no sheet). Só
 * leitura: os limiares moram no código das regras, não nos ajustes.
 */
export const LIVE_RULE_DEFS: readonly RuleDef[] = [
  ...MEDIA_RULES,
  ...ALERT_RULES.map(([rule, name, severity, description, escalation]) =>
    ruleDef({
      id: `RA-${rule}`,
      name,
      description: `${description} O servidor da automação confere a cada minuto.`,
      category: 'dados',
      scope: AUTOMACAO_TARGET,
      severity,
      kind: 'risk',
      owners: ['SO', 'SR'],
      ...(escalation ? { escalation } : {}),
      actionTemplate: 'investigate',
      impactFormula: 'Sem valor em R$: operação da automação do Instagram.',
    }),
  ),
];

/** Troca o catálogo da simulação (R01…) pelo das regras que rodam. */
export function withLiveRules<B>(boot: B): B {
  if (!boot || typeof boot !== 'object' || !('ruleDefs' in boot)) return boot;
  return { ...boot, ruleDefs: [...LIVE_RULE_DEFS] };
}

/**
 * "Simular nos últimos 7 dias" no modo ao vivo: os limiares são os que rodam (só leitura), então o
 * resultado é quantas vezes a regra disparou de verdade. Alertas: abertos e resolvidos nos 7 dias
 * (servidor). Mídia: as decisões que o Painel registrou (uma por campanha por dia).
 */
export function liveRuleFires(
  ruleId: string,
  alerts: ServerAlerts,
  seen: SeenMap,
  from: number,
): { fired: number; targets: { name: string; at: number }[] } {
  const targets: { name: string; at: number }[] = [];
  if (ruleId.startsWith('RA-')) {
    const rule = ruleId.slice(3);
    for (const a of [...alerts.open, ...alerts.resolved])
      if (a.rule === rule && (a.resolvedAt ?? a.lastSeenAt) >= from)
        targets.push({ name: a.title, at: a.firstSeenAt });
  } else {
    for (const [key, v] of Object.entries(seen)) {
      const parts = keyParts(key);
      if (parts?.ruleId !== ruleId || v.lastAt < from) continue;
      targets.push({ name: v.snap?.title ?? parts.targetId, at: v.firstAt });
    }
  }
  targets.sort((a, b) => b.at - a.at);
  return { fired: targets.length, targets };
}

// ---------- estado (feito / adiado / descartado) ----------

export function withState(d: Decision, states: DecisionStates, now: number): Decision {
  const s = states[d.dedupKey];
  if (!s) return d;
  if (s.status === 'snoozed' && (s.until ?? 0) <= now) return d;
  return {
    ...d,
    status: s.status,
    updatedAt: s.at,
    ...(s.until ? { snoozedUntil: s.until } : {}),
    ...(s.status === 'dismissed' && s.reason ? { dismissReason: s.reason as Decision['dismissReason'] } : {}),
    history: [{ at: s.at, by: s.by ?? 'system', from: 'new', to: s.status }],
  };
}

/** Retrato da decisão para o servidor (título, regra, alvo e impacto do momento). */
export function snapOf(d: Decision): DecisionSnap {
  return {
    title: d.title,
    ruleId: d.ruleId,
    severity: d.severity,
    kind: d.kind,
    rationale: d.rationale.slice(0, 400),
    ...(d.target ? { target: d.target } : {}),
    impact: d.impact,
    createdAt: d.createdAt,
  };
}

/**
 * Regras de mídia: a idade conta da 1ª vez que o Painel viu a decisão no dia (servidor), não do
 * instante do cálculo. Alertas da automação já trazem a 1ª vez do próprio alerta.
 */
export function withSeen(d: Decision, seen: SeenMap, now: number): Decision {
  const first = seen[d.dedupKey]?.firstAt;
  if (!d.ruleId.startsWith('RM') || first == null) return d;
  const at = Math.min(first, now);
  return at === d.createdAt ? d : { ...d, createdAt: at, updatedAt: at };
}

const CLOSED: ReadonlySet<DecisionStatus> = new Set(['executed', 'dismissed', 'resolved', 'expired']);

/** Nome da regra para decisões antigas sem retrato. */
const RULE_NAME: Record<string, string> = {
  RM1: 'Pausar campanha sem venda',
  RM2: 'Escalar campanha',
  RM3: 'Conta gasta mais do que vende',
  RM4: 'Campanha com retorno baixo',
  RM5: 'Campanha nova sem venda',
};

/** Nome da regra pelo id: as de mídia pela tabela; as de alerta (`RA-*`) pelo catálogo ao vivo. */
function ruleNameOf(ruleId: string): string {
  return RULE_NAME[ruleId] ?? LIVE_RULE_DEFS.find((r) => r.id === ruleId)?.name ?? ruleId;
}

/** Partes da chave: `RM2:<campanha>:<dia>` ou `RA:<regra>:<id do alerta>`. */
export function keyParts(
  key: string,
): { ruleId: Decision['ruleId']; targetId: string; day: string | null } | null {
  const m = /^(RM\d):(.+):(\d{4}-\d{2}-\d{2})$/.exec(key);
  if (m) return { ruleId: m[1] as Decision['ruleId'], targetId: m[2] as string, day: m[3] as string };
  const a = /^RA:([^:]+):(.+)$/.exec(key);
  if (a) return { ruleId: `RA-${a[1]}`, targetId: a[2] as string, day: null };
  return null;
}

export interface PastSource {
  state?: DecisionState;
  seen?: SeenEntry;
  alert?: ServerAlert;
}

/**
 * Decisão que não está mais no "Agora" (a regra parou de disparar, o dia virou ou o alerta fechou),
 * montada do estado marcado, do retrato e do alerta resolvido. Fechada: feita, ignorada, resolvida
 * sozinha ou expirada com o dia. Sem retrato (marcas antigas), título pela regra e pelo alvo, e o
 * impacto "não registrado" (nunca R$ 0 como se fosse medido).
 */
export function pastDecision(
  key: string,
  src: PastSource,
  now: number,
  names: ReadonlyMap<string, string> = new Map(),
): Decision | null {
  const parts = keyParts(key);
  if (!parts) return null;
  const { state, seen, alert } = src;
  const snap = state?.snap ?? seen?.snap ?? null;
  const today = dateKeyOf(now);
  let status: DecisionStatus;
  let at: number;
  let by = 'system';
  if (state && (state.status === 'executed' || state.status === 'dismissed')) {
    status = state.status;
    at = state.at;
    by = state.by ?? 'system';
  } else if (alert && alert.status === 'resolved' && alert.resolvedAt != null) {
    // Resolvido à mão na área Automações conta como feito; pela varredura, resolvido sozinho.
    const human = alert.resolvedBy != null && alert.resolvedBy !== 'auto';
    status = human ? 'executed' : 'resolved';
    at = alert.resolvedAt;
    if (human) by = alert.resolvedBy ?? 'system';
  } else if (seen) {
    // Regra de mídia de outro dia: expirou com o dia. Do mesmo dia (ou alerta): sumiu sozinha.
    status = parts.day != null && parts.day < today ? 'expired' : 'resolved';
    at = seen.lastAt;
  } else if (state) {
    status = parts.day != null && parts.day < today ? 'expired' : 'resolved';
    at = state.at;
  } else return null;

  const ruleId = snap?.ruleId ?? parts.ruleId;
  const isAccount = parts.ruleId === 'RM3';
  const isAlert = parts.ruleId.startsWith('RA-');
  // Alerta da automação: o alvo é o alerta (pelo id do servidor), não uma campanha.
  const targetName = isAccount
    ? `conta ${accountName(parts.targetId)}`
    : isAlert
      ? `alerta ${parts.targetId}`
      : (names.get(parts.targetId) ?? `campanha ${parts.targetId}`);
  const title =
    snap?.title ??
    alert?.title ??
    `${ruleNameOf(parts.ruleId)} · ${targetName}${state ? `, marcada em ${ddmm(state.at)}` : ''}`;
  const created =
    seen?.firstAt ?? snap?.createdAt ?? alert?.firstSeenAt ?? (parts.day ? dayStart(parts.day) : at);
  const target: Decision['target'] | undefined =
    fixAlertTarget(snap?.target) ??
    (isAlert
      ? alertTarget(parts.targetId, alert?.action?.label, alert?.action?.href)
      : isAccount
        ? undefined
        : {
            type: 'campaign',
            id: parts.targetId,
            name: targetName,
            route: `/trafego/campanha/${parts.targetId}`,
          });
  const severity: Severity =
    snap?.severity ??
    (alert ? (ALERT_SEVERITY[alert.severity] ?? 'P2') : parts.ruleId === 'RM1' || isAccount ? 'P1' : 'P2');
  const d = base({
    key,
    ruleId,
    severity,
    kind: snap?.kind ?? (parts.ruleId === 'RM2' ? 'opportunity' : 'risk'),
    title,
    rationale: snap?.rationale ?? alert?.detail ?? '',
    whyText: snap
      ? 'Decisão que já saiu do "Agora": fica no histórico com o título e o impacto da época.'
      : 'Decisão de antes do Painel guardar o retrato: o título vem da regra e do alvo, sem o impacto da época.',
    evidence: [],
    ...(target ? { target } : {}),
    // Alerta nunca teve valor em R$ (operação da automação): não é "impacto não registrado".
    impact:
      snap?.impact ??
      (isAlert
        ? { ...ALERT_IMPACT }
        : { cents: 0, label: 'impacto não registrado', calc: '', horizon: 'once' }),
    action: { kind: 'investigate', executable: false, guardrails: [] },
    createdAt: Math.min(created, at),
    priority: 0,
  });
  return {
    ...d,
    status,
    updatedAt: at,
    ...(status === 'dismissed' && state?.reason
      ? { dismissReason: state.reason as Decision['dismissReason'] }
      : {}),
    history: [{ at, by, from: 'new', to: status }],
  };
}

/** A decisão fecha (feita, ignorada, resolvida, expirada) e sai do "Agora". */
export const isClosedStatus = (s: DecisionStatus): boolean => CLOSED.has(s);

/** Status que o comando leva (o painel não executa nada na Meta: "aplicar" = marcar como feito). */
export function statusFor(cmd: DecisionCommand): DecisionStatus | null {
  switch (cmd.kind) {
    case 'apply':
      return 'executed';
    case 'snooze':
      return 'snoozed';
    case 'dismiss':
      return 'dismissed';
    case 'undo':
      return 'new';
    case 'seen':
      return null;
  }
}

// ---------- atividade ----------

const PRODUCT: Record<RealOrderRow['kind'], string> = {
  front: 'Curso',
  upsell: '20X',
  premium: 'Premium',
  outro: 'Produto',
};
const CHANNEL: Record<RealOrderRow['channel'], string> = {
  automacao: 'Automação do direct',
  ig_bio: 'Bio do Instagram',
  meta_ads: 'Anúncio',
  outro: 'Outro link',
  sem_origem: 'Sem origem',
};
const payLabel = (p: string | null) => {
  const s = (p ?? '').toLowerCase();
  if (s === 'pix') return 'Pix';
  if (s.includes('boleto')) return 'Boleto';
  if (!s) return 'Pagamento';
  return 'Cartão';
};
function origin(o: RealOrderRow): string {
  if (o.channel === 'meta_ads' && o.campaignName) return `Anúncio · ${o.adName ?? o.campaignName}`;
  return CHANNEL[o.channel];
}

/**
 * Feed de atividade com os pedidos reais da Kiwify (mais novo primeiro). `refunds` é a lista de
 * reembolsos datada pelo reembolso (servidor): com ela, os reembolsos saem da lista por data da
 * compra (nada contado duas vezes) e entram na hora em que foram feitos.
 */
export function realActivity(rows: RealOrderRow[], refunds?: RealOrderRow[] | null): ActivityItem[] {
  const out: ActivityItem[] = [];
  const list = refunds ? [...rows.filter((o) => !isRefundLike(o.status)), ...refunds] : rows;
  for (const o of list) {
    const prod = PRODUCT[o.kind];
    const val = brl(o.baseCents ?? o.grossCents);
    // Produto desconhecido ("outro") não vira pedido do painel (toOrder): sem link para o detalhe.
    const route = o.kind === 'outro' ? undefined : `/vendas/${o.id}`;
    const raw = o.rawStatus ?? '';
    if (o.status === 'paid')
      out.push({
        id: `act_sale_${o.id}`,
        at: o.at,
        // A consulta Premium tem tipo e ícone próprios; só o 20X usa o do 20X (produto desconhecido
        // fica com o ícone comum de venda).
        kind: o.kind === 'upsell' ? 'upsell' : o.kind === 'premium' ? 'premium' : 'sale',
        title: `Nova venda · ${prod} · ${val}`,
        subtitle: `${payLabel(o.payment)} · ${origin(o)}`,
        value: o.baseCents ?? o.grossCents,
        ...(route ? { refRoute: route } : {}),
      });
    else if (o.status === 'waiting_payment')
      out.push({
        id: `act_pix_${o.id}`,
        at: o.at,
        kind: payLabel(o.payment) === 'Boleto' ? 'boleto_created' : 'pix_created',
        title: `${payLabel(o.payment) === 'Boleto' ? 'Boleto gerado' : 'Pix gerado'} · ${prod} · ${val}`,
        subtitle: `Aguardando pagamento · ${origin(o)}`,
        value: o.baseCents ?? o.grossCents,
        ...(route ? { refRoute: route } : {}),
      });
    else if (o.status === 'refused')
      out.push({
        id: `act_ref_${o.id}`,
        at: o.at,
        kind: 'refused',
        title: `Cartão recusado · ${prod} · ${val}`,
        subtitle: origin(o),
        value: o.baseCents ?? o.grossCents,
        ...(route ? { refRoute: route } : {}),
      });
    else if (isRefundLike(o.status)) {
      const cb = o.status !== 'refunded';
      out.push({
        id: `act_${cb ? 'cb' : 'rf'}_${o.id}`,
        at: orderDates(o).refundedAt ?? o.at,
        kind: cb ? 'chargeback' : 'refund',
        title: `${cb ? 'Chargeback' : 'Reembolso'} · ${prod} · ${val}`,
        subtitle: origin(o),
        value: o.baseCents ?? o.grossCents,
        ...(route ? { refRoute: route } : {}),
      });
    } else if (raw === 'abandoned')
      out.push({
        id: `act_cart_${o.id}`,
        at: o.at,
        kind: 'cart',
        title: `Carrinho abandonado · ${prod}`,
        subtitle: origin(o),
        // Só o curso e o 20X entram na fila de recuperação (o Premium ainda não tem mensagem própria).
        ...(o.kind === 'front' || o.kind === 'upsell' ? { refRoute: '/recuperacao' } : {}),
      });
  }
  return out.sort((a, b) => b.at - a.at);
}

// ---------- módulo do modo ao vivo ----------

/** De quanto em quanto tempo o "Agora" se refaz com os alertas e as regras de mídia (o servidor varre a cada minuto). */
export const DECISIONS_REFRESH_MS = 60_000;

/** Decisões ("Agora"), "O que mudou" e o contador de decisões abertas do snapshot, com dados reais. */
export function decisionsModule(ctx: RealCtx): RealModule {
  const { clock } = ctx;
  let cache: { at: number; p: Promise<Decision[]> } | null = null;
  let states: DecisionStates = {};
  /** 1ª vez de cada decisão (servidor). */
  let seenFirst: SeenMap = {};
  // Última leitura boa de cada fonte: falha passageira não esvazia o "Agora" (nem volta tudo a "nova").
  let lastAlerts: ServerAlerts = { open: [], resolved: [] };
  let lastMedia: Decision[] = [];
  let lastList: Decision[] | null = null;
  /** Nome das campanhas (para o histórico de decisões antigas sem retrato). */
  const names = new Map<string, string>();

  /** Decisões de agora: alertas da automação + regras de mídia sobre Meta e Kiwify reais. */
  const loadDecisions = (): Promise<Decision[]> => {
    const now = clock();
    if (cache && now - cache.at < CACHE_MS) return cache.p;
    const today = dayStart(todayKey(now));
    // Últimos 30 dias de São Paulo: hoje e os 29 dias antes, desde a meia-noite.
    const from = today - (MEDIA_WINDOW_DAYS - 1) * DAY;
    const p = Promise.all([
      fetchAlerts().catch(() => lastAlerts),
      ctx.loadInsights(from, now, 'campaign'),
      ctx.loadInsights(today, now, 'campaign'),
      fetchDecisionStates().catch(() => states),
    ]).then(async ([alerts, all, td, st]) => {
      states = st;
      lastAlerts = alerts;
      if (all) {
        for (const r of all.rows) if (r.level === 'campaign') names.set(r.id, r.name);
        lastMedia = mediaDecisions({ campaigns: all.rows, today: td?.rows ?? [], now, from });
      }
      const list = [...alertDecisions(alerts.open, now), ...lastMedia];
      // Avisa o servidor do que está na tela e recebe a 1ª vez de cada uma (idade de verdade).
      const seen = await markSeen(list.map((d) => ({ key: d.dedupKey, snap: snapOf(d) }))).catch(
        () => seenFirst,
      );
      seenFirst = { ...seenFirst, ...seen };
      const out = list.map((d) => withState(withSeen(d, seenFirst, now), states, now));
      lastList = out;
      return out;
    });
    cache = { at: now, p };
    p.catch(() => {
      cache = null;
    });
    return p;
  };
  const current = (): Promise<Decision[]> =>
    loadDecisions().catch((e: unknown) => {
      if (lastList) return lastList;
      throw e;
    });

  const seenCache = new Map<string, { at: number; p: Promise<SeenMap> }>();
  /** Fim do minuto seguinte (a mesma consulta dentro do minuto aproveita o cache). */
  const nextMinute = (t: number) => Math.ceil((t + 60_000) / 60_000) * 60_000;
  const loadSeen = (from: number, to: number): Promise<SeenMap> => {
    const key = `${from}|${to}`;
    const hit = seenCache.get(key);
    if (hit && clock() - hit.at < CACHE_MS) return hit.p;
    const p = fetchSeen(from, to).catch(() => ({}) as SeenMap);
    seenCache.set(key, { at: clock(), p });
    return p;
  };

  /**
   * Decisões fechadas que já saíram do "Agora" e fecharam em [from, to): marcadas pela equipe,
   * alertas resolvidos e regras que pararam de disparar (ou expiraram com o dia).
   */
  async function pastDecisions(from: number, to: number, now: Decision[]): Promise<Decision[]> {
    const t = clock();
    const seen = await loadSeen(from, Math.min(to, nextMinute(t)));
    const live = new Set(now.map((d) => d.dedupKey));
    const alerts = new Map<string, ServerAlert>(
      [...lastAlerts.resolved, ...lastAlerts.open].map((a) => [`RA:${a.rule}:${a.id}`, a]),
    );
    const keys = new Set([...Object.keys(states), ...Object.keys(seen), ...alerts.keys()]);
    const out: Decision[] = [];
    for (const key of keys) {
      if (live.has(key)) continue;
      const alert = alerts.get(key);
      // Alerta ainda aberto, mas fora da lista (ex.: leitura antiga): não vira histórico.
      if (alert?.status === 'open') continue;
      const src: PastSource = {
        ...(states[key] ? { state: states[key] } : {}),
        ...(seen[key] ? { seen: seen[key] } : {}),
        ...(alert ? { alert } : {}),
      };
      const d = pastDecision(key, src, t, names);
      if (d && isClosedStatus(d.status) && d.updatedAt >= from && d.updatedAt < to) out.push(d);
    }
    return out;
  }

  async function listDecisions(q: ListQuery): Promise<Page<Decision>> {
    const now = clock();
    const rows0 = await current();
    const from = dayStart(q.range.from);
    const to = dayStart(q.range.to) + DAY;
    // Abertas e adiadas só existem agora (aba Agora); as fechadas seguem o período, pela hora da marca.
    const includesToday = q.range.to >= todayKey(now);
    const inRange = (d: Decision) => d.updatedAt >= from && d.updatedAt < to;
    let rows = rows0.filter((d) => (isClosedStatus(d.status) ? inRange(d) : includesToday));
    rows = [...rows, ...(await pastDecisions(from, to, rows0))];
    if (q.status?.length) rows = rows.filter((d) => q.status?.includes(d.status));
    rows = [...rows].sort((a, b) => b.priorityScore - a.priorityScore || b.updatedAt - a.updatedAt);
    return paginate(rows, q);
  }

  async function getDecision(id: string): Promise<Decision | null> {
    const now = await current().catch(() => [] as Decision[]);
    const hit = now.find((d) => d.id === id);
    if (hit) return hit;
    const start = dayStart(todayKey(clock()));
    return (await pastDecisions(start - 30 * DAY, start + DAY, now)).find((d) => d.id === id) ?? null;
  }

  async function decide(cmd: DecisionCommand): Promise<Decision> {
    const d = await getDecision(cmd.id);
    if (!d) throw Object.assign(new Error('Decisão não encontrada'), { code: 'not_found' });
    const now = clock();
    const to = statusFor(cmd);
    if (to == null) return { ...d, status: d.status === 'new' ? 'seen' : d.status, seenAt: now };
    // Retrato junto com a marca: o Histórico mostra a decisão mesmo depois que a regra parar.
    const snap = snapOf(d);
    await saveDecisionState(d.dedupKey, to, {
      ...(cmd.kind === 'snooze' ? { until: cmd.until } : {}),
      ...(cmd.kind === 'dismiss' && cmd.reason ? { reason: cmd.reason } : {}),
      ...(to !== 'new' ? { snap } : {}),
    });
    // Alerta da automação marcado como feito: silencia no servidor também.
    if (d.ruleId.startsWith('RA-') && (to === 'executed' || to === 'dismissed') && d.target?.id)
      await ackAlert(d.target.id).catch(() => undefined);
    const next = { ...states };
    if (to === 'new') delete next[d.dedupKey];
    else
      next[d.dedupKey] = {
        status: to,
        at: now,
        ...(cmd.kind === 'snooze' ? { until: cmd.until } : {}),
        ...(cmd.kind === 'dismiss' && cmd.reason ? { reason: cmd.reason } : {}),
        snap,
      };
    states = next;
    cache = null;
    return {
      ...d,
      status: to === 'new' ? 'new' : to,
      updatedAt: now,
      ...(cmd.kind === 'snooze' ? { snoozedUntil: cmd.until } : {}),
      history: [...d.history, { at: now, by: 'system', from: d.status, to }],
    };
  }

  /** Quantas se resolveram sozinhas desde `since`: alertas fechados pela varredura e regras que pararam. */
  async function autoResolvedSince(since: number, now: number, list: Decision[]): Promise<number> {
    const alerts = lastAlerts.resolved.filter(
      (a) => a.resolvedBy === 'auto' && a.resolvedAt != null && a.resolvedAt >= since && a.resolvedAt <= now,
    ).length;
    // Regra de mídia: a mesma regra e alvo (sem o dia) não está mais na tela e ninguém marcou.
    const baseOf = (k: string) => k.replace(/:\d{4}-\d{2}-\d{2}$/, '');
    const live = new Set(list.map((d) => baseOf(d.dedupKey)));
    const seen = await loadSeen(since, nextMinute(now));
    const gone = new Set<string>();
    for (const [k, v] of Object.entries(seen)) {
      if (!k.startsWith('RM') || live.has(baseOf(k))) continue;
      if (v.lastAt < since || v.lastAt > now - 2 * 60_000) continue;
      const st = states[k]?.status;
      if (st === 'executed' || st === 'dismissed') continue;
      gone.add(baseOf(k));
    }
    return alerts + gone.size;
  }

  async function getChangesSince(since: number): Promise<ChangesSince> {
    const now = clock();
    const [rows, decisions] = await Promise.all([
      ctx
        .load(Math.floor(since / 60_000) * 60_000, Math.ceil(now / 60_000) * 60_000)
        .catch(() => [] as RealOrderRow[]),
      current().catch(() => [] as Decision[]),
    ]);
    const paid = rows.filter((o) => o.status === 'paid' && o.at >= since);
    return {
      since,
      sales: paid.length,
      revenueGross: paid.reduce((s, o) => s + revenueOf(o), 0),
      // createdAt = 1ª vez que a decisão apareceu (servidor): a mesma decisão não conta como nova a cada visita.
      newDecisions: decisions.filter((d) => d.createdAt >= since && d.status === 'new').length,
      autoResolved: await autoResolvedSince(since, now, decisions).catch(() => 0),
      actionsByOthers: Object.entries(states)
        .filter(([, v]) => v.at >= since && v.by)
        .map(([k, v]) => ({
          at: v.at,
          by: v.by ?? '',
          label: `${v.status === 'executed' ? 'Marcou como feito' : v.status === 'snoozed' ? 'Adiou' : 'Descartou'}: ${
            decisions.find((d) => d.dedupKey === k)?.title ?? v.snap?.title ?? 'decisão'
          }`,
        }))
        .sort((a, b) => b.at - a.at)
        .slice(0, 10),
      // Quedas das fontes ainda não são registradas no modo ao vivo (a lista fica vazia, sem linha).
      outages: [],
    };
  }

  /**
   * "Simular" da regra: quantas vezes ela disparou de verdade nos últimos 7 dias (os limiares são só
   * leitura). Servidor com erro lança: o sheet diz que não deu para simular, nunca "0 vezes".
   */
  async function simulateRule(ruleId: RuleDef['id']) {
    const now = clock();
    const from = now - 7 * DAY;
    const ra = ruleId.startsWith('RA-');
    const [alerts, seen] = await Promise.all([
      ra ? fetchAlerts() : Promise.resolve(lastAlerts),
      ra ? Promise.resolve({} as SeenMap) : fetchSeen(from, nextMinute(now)),
    ]);
    return liveRuleFires(ruleId, alerts, seen, from);
  }

  return {
    overrides: { getDecision, decide, getChangesSince, simulateRule },
    list: (kind, q) => (kind === 'decisions' ? listDecisions(q) : undefined),
    // Regras (aba Regras, sheet da regra, nome no sheet da decisão): as que rodam, não as da simulação.
    patchBoot: async (boot) => withLiveRules(boot),
    onMediaSync: () => {
      cache = null;
    },
    // A cada minuto o "Agora" se refaz: alerta que fechou sai, alerta novo entra e o título (681, 185…)
    // acompanha a varredura. Mudou o "Agora" (decisão nova ou que saiu, status, título) ou chegou aviso
    // novo da Kiwify, o `resync` faz as listas de hoje (decisões, atividade, pedidos, recuperação)
    // pedirem de novo, sem recarregar a página. Só a conferência andou (a varredura do servidor reescreve
    // o `lastSeenAt` de todo alerta aberto a cada minuto), o caminho é leve: um `decision.upsert` por
    // cartão, para o "conferido há" andar sem refazer as outras listas em toda aba aberta (e na TV).
    subscribe(emit) {
      let sig: string | null = null;
      let seenAt = new Map<string, number>();
      const tick = (fresh: boolean) => {
        if (fresh) {
          cache = null;
          seenCache.clear();
        }
        void loadDecisions()
          .then((list) => {
            const next = JSON.stringify([
              ctx.lastEventAt?.() ?? null,
              list.map((d) => [d.id, d.status, d.title, d.updatedAt]),
            ]);
            const nextSeen = new Map(list.map((d) => [d.id, d.lastSeenAt ?? 0]));
            if (sig != null && next !== sig) emit({ type: 'resync', topic: 'kpis', data: { reason: 'gap' } });
            else if (sig != null)
              for (const d of list)
                if ((d.lastSeenAt ?? 0) !== (seenAt.get(d.id) ?? 0))
                  emit({ type: 'decision.upsert', topic: 'decisions', data: d });
            sig = next;
            seenAt = nextSeen;
          })
          .catch(() => undefined);
      };
      tick(false);
      const timer = setInterval(() => tick(true), DECISIONS_REFRESH_MS);
      return () => clearInterval(timer);
    },
    async patchSnapshot(_q, snap) {
      const decs = await current().catch(() => [] as Decision[]);
      const openD = { p0: 0, p1: 0, p2: 0, p3: 0 };
      for (const d of decs)
        if (['new', 'seen', 'accepted', 'executing', 'failed'].includes(d.status))
          openD[d.severity.toLowerCase() as 'p0' | 'p1' | 'p2' | 'p3'] += 1;
      snap.openDecisions = openD;
    },
  };
}
